import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormHelperText from '@material-ui/core/FormHelperText';

type ComponentOption = {
  label: string;
  value: string;
};

const sanitizeSystemRef = (systemRef?: string) => {
  if (!systemRef) {
    return undefined;
  }

  try {
    const { namespace, name } = parseEntityRef(systemRef, {
      defaultKind: 'system',
      defaultNamespace: 'default',
    });
    return namespace && namespace !== 'default' ? `${namespace}/${name}` : name;
  } catch (error) {
    const withoutKind = systemRef.replace(/^system:/i, '');
    return withoutKind.replace(/^default\//i, '');
  }
};

const useComponentOptions = (systemRef?: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [options, setOptions] = useState<ComponentOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isActive = true;
    const loadComponents = async () => {
      const system = sanitizeSystemRef(systemRef);
      if (!system) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await catalogApi.getEntities({
          filter: { kind: 'Component', 'spec.system': system },
        });

        if (!isActive) {
          return;
        }

        const mapped = response.items
          .map(entity => ({
            value: stringifyEntityRef(entity),
            label: entity.metadata.title ?? entity.metadata.name ?? '',
          }))
          .filter(option => option.label || option.value)
          .sort((a, b) => a.label.localeCompare(b.label));

        setOptions(mapped);
      } catch (error) {
        if (isActive) {
          setOptions([]);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadComponents();

    return () => {
      isActive = false;
    };
  }, [catalogApi, systemRef]);

  return { options, loading };
};

export const SystemComponentPickerSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      placeholder: {
        type: 'string',
        title: 'Placeholder',
        default: 'Select a component',
      },
    },
  },
  returnValue: { type: 'string' },
};

export const SystemComponentPicker = (
  props: FieldExtensionComponentProps<string, { placeholder?: string }>,
) => {
  const {
    formContext,
    onChange,
    rawErrors,
    required,
    formData,
    schema,
    uiSchema,
  } = props;

  const systemRef: string | undefined = formContext?.formData?.systemRef;
  const { options, loading } = useComponentOptions(systemRef);
  const previousSystemRef = useRef<string | undefined>(systemRef);

  useEffect(() => {
    if (previousSystemRef.current !== systemRef) {
      onChange(undefined);
    }
    previousSystemRef.current = systemRef;
  }, [systemRef, onChange]);

  const selectedOption = useMemo(
    () => options.find(option => option.value === formData) ?? null,
    [formData, options],
  );

  useEffect(() => {
    if (
      selectedOption === null &&
      formData &&
      !options.some(option => option.value === formData)
    ) {
      onChange(undefined);
    }
  }, [selectedOption, formData, options, onChange]);

  const helperText =
    uiSchema?.['ui:options']?.placeholder ||
    (!systemRef ? 'Select a system to load components' : undefined);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !selectedOption}
      fullWidth
    >
      <Autocomplete
        options={options}
        getOptionLabel={option => option.label}
        getOptionSelected={(option, value) => option.value === value.value}
        value={selectedOption}
        loading={loading}
        onChange={(_, value) => {
          onChange(value?.value);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title}
            variant="standard"
            placeholder={uiSchema?.['ui:options']?.placeholder}
            helperText={helperText}
            disabled={!systemRef}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {schema.description && <FormHelperText>{schema.description}</FormHelperText>}
    </FormControl>
  );
};
